import { useState } from 'react'
import { FileDown } from 'lucide-react'
import { format } from 'date-fns'
import jsPDF from 'jspdf'
import { calculateVehicleScore, getScoreTier } from '../lib/garageScore'

const RDC_ORANGE = '#FF4400'
const RDC_FOREST = '#1a4d2e'
const RDC_TAUPE = '#999999'
const RDC_CREAM = '#f5f2ed'
const RDC_GRAY = '#e5e5e5'

export default function VehicleHistoryPDF({
  vehicle = {},
  serviceRecords = [],
  documents = [],
  valuations = [],
  ownerInfo = {}
}) {
  const [isGenerating, setIsGenerating] = useState(false)

  const handleDownloadPDF = async () => {
    try {
      setIsGenerating(true)
      await generatePDF()
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF: ' + (error.message || 'Unknown error'))
    } finally {
      setIsGenerating(false)
    }
  }

  const generatePDF = () => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 12
    const contentWidth = pageWidth - 2 * margin

    let yPosition = margin

    // Helper functions
    const addText = (text, x, y, options = {}) => {
      pdf.setFont('Helvetica', options.bold ? 'bold' : 'normal')
      pdf.setFontSize(options.size || 11)
      pdf.setTextColor(options.color || '0,0,0')
      pdf.text(text, x, y, options)
    }

    const addHeading = (text, y) => {
      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(13)
      pdf.setTextColor(RDC_FOREST)
      pdf.text(text, margin, y)
      // Add underline
      pdf.setDrawColor(RDC_ORANGE)
      pdf.setLineWidth(0.5)
      pdf.line(margin, y + 1.5, margin + contentWidth, y + 1.5)
      return y + 7
    }

    const getTextWidth = (text, fontSize = 11) => {
      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(fontSize)
      return pdf.getTextWidth(text)
    }

    const checkNewPage = (spaceNeeded) => {
      if (yPosition + spaceNeeded > pageHeight - margin) {
        pdf.addPage()
        yPosition = margin
      }
    }

    // Calculate vehicle score
    const photoCount = documents?.filter(d => d.type === 'photo')?.length || 0
    const scoreData = calculateVehicleScore(
      vehicle,
      serviceRecords.length,
      documents.length,
      valuations.length,
      photoCount
    )

    // ========== PAGE 1: Header and Vehicle Overview ==========

    // Header
    pdf.setFont('Helvetica', 'bold')
    pdf.setFontSize(18)
    pdf.setTextColor(RDC_ORANGE)
    pdf.text('RDC Digital Garage', margin, yPosition)
    yPosition += 7

    pdf.setFont('Helvetica', 'normal')
    pdf.setFontSize(11)
    pdf.setTextColor(RDC_TAUPE)
    pdf.text('Vehicle Report', margin, yPosition)
    yPosition += 6

    // Generated date
    pdf.setFont('Helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(RDC_TAUPE)
    pdf.text(`Generated: ${format(new Date(), 'MMMM dd, yyyy')}`, margin, yPosition)
    yPosition += 8

    // Vehicle Title
    pdf.setFont('Helvetica', 'bold')
    pdf.setFontSize(16)
    pdf.setTextColor(RDC_FOREST)
    const vehicleTitle = `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}${vehicle.trim ? ` ${vehicle.trim}` : ''}`
    pdf.text(vehicleTitle, margin, yPosition)
    yPosition += 8

    // Vehicle Overview Section
    yPosition = addHeading('VEHICLE OVERVIEW', yPosition)

    // Create 2-column layout for vehicle details
    const col1X = margin
    const col2X = margin + contentWidth / 2

    const detailsFields = [
      { label: 'VIN', value: vehicle.vin || 'N/A' },
      { label: 'Color / Interior', value: `${vehicle.exterior_color || vehicle.color || 'N/A'} / ${vehicle.interior_color || 'N/A'}` },
      { label: 'Mileage', value: (vehicle.current_mileage || vehicle.mileage) ? `${(vehicle.current_mileage || vehicle.mileage).toLocaleString()} miles` : 'N/A' },
      { label: 'Fuel Type / Powertrain', value: `${vehicle.fuel_type || 'N/A'} / ${vehicle.powertrain || 'N/A'}` }
    ]

    // Left column
    detailsFields.slice(0, 2).forEach(field => {
      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(9)
      pdf.setTextColor(RDC_TAUPE)
      pdf.text(field.label, col1X, yPosition)
      yPosition += 3

      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.setTextColor('0,0,0')
      const wrapped = pdf.splitTextToSize(field.value, contentWidth / 2 - 2)
      pdf.text(wrapped, col1X, yPosition)
      yPosition += wrapped.length * 3 + 1
    })

    // Reset Y for right column
    yPosition = yPosition - (detailsFields.slice(0, 2).reduce((sum, f) => sum + (f.value.length > 20 ? 6 : 3), 0) + 2)
    const rightColY = yPosition

    // Right column
    detailsFields.slice(2, 4).forEach((field, idx) => {
      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(9)
      pdf.setTextColor(RDC_TAUPE)
      pdf.text(field.label, col2X, rightColY + idx * 8)

      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.setTextColor('0,0,0')
      const wrapped = pdf.splitTextToSize(field.value, contentWidth / 2 - 2)
      pdf.text(wrapped, col2X, rightColY + idx * 8 + 3)
    })

    yPosition = rightColY + 16

    // ========== GARAGE SCORE ==========
    checkNewPage(30)
    yPosition = addHeading('GARAGE SCORE', yPosition)

    // Score circle/badge
    const scorePercentage = scoreData.score
    const tier = scoreData.tierInfo

    // Score display
    pdf.setFont('Helvetica', 'bold')
    pdf.setFontSize(32)
    pdf.setTextColor(RDC_ORANGE)
    pdf.text(`${scorePercentage}%`, margin, yPosition + 7)

    pdf.setFont('Helvetica', 'bold')
    pdf.setFontSize(12)
    pdf.setTextColor(RDC_FOREST)
    pdf.text(tier.name, margin, yPosition + 15)

    pdf.setFont('Helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(RDC_TAUPE)
    const tierDescription = pdf.splitTextToSize(tier.description, contentWidth * 0.6)
    pdf.text(tierDescription, margin, yPosition + 19)

    yPosition += 25

    // ========== SERVICE HISTORY ==========
    checkNewPage(35)
    yPosition = addHeading('SERVICE HISTORY', yPosition)

    if (serviceRecords && serviceRecords.length > 0) {
      // Service table header
      const tableX = margin
      const dateColWidth = 30
      const typeColWidth = 40
      const providerColWidth = 35
      const costColWidth = 25
      const notesColWidth = contentWidth - dateColWidth - typeColWidth - providerColWidth - costColWidth - 3

      // Header row
      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(9)
      pdf.setTextColor('255,255,255')
      pdf.setFillColor(RDC_FOREST)

      const headers = ['Date', 'Type', 'Provider', 'Cost', 'Notes']
      const colWidths = [dateColWidth, typeColWidth, providerColWidth, costColWidth, notesColWidth]
      let headerX = tableX

      headers.forEach((header, idx) => {
        pdf.rect(headerX, yPosition - 2, colWidths[idx], 5, 'F')
        pdf.text(header, headerX + 1, yPosition + 1)
        headerX += colWidths[idx]
      })

      yPosition += 6

      // Service rows
      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(8)
      pdf.setTextColor('0,0,0')

      serviceRecords.forEach((record, idx) => {
        checkNewPage(5)

        const date = format(new Date(record.service_date), 'MM/dd/yyyy')
        const type = record.service_type || 'Service'
        const provider = record.performed_by || record.vendor_name || 'N/A'
        const cost = `$${(record.cost || record.total_cost || 0).toLocaleString()}`
        const notes = record.notes || record.description || ''

        const rowData = [date, type, provider, cost, notes]

        let maxHeight = 4
        const wrappedCols = rowData.map((cell, idx) => {
          const wrapped = pdf.splitTextToSize(String(cell), colWidths[idx] - 2)
          maxHeight = Math.max(maxHeight, wrapped.length * 3)
          return wrapped
        })

        let colX = tableX
        // Alternate row background
        if (idx % 2 === 0) {
          pdf.setFillColor(245, 242, 237)
          pdf.rect(tableX, yPosition - 2, contentWidth, maxHeight, 'F')
        }

        wrappedCols.forEach((wrapped, idx) => {
          pdf.text(wrapped, colX + 1, yPosition)
          colX += colWidths[idx]
        })

        yPosition += maxHeight
      })

      // Service summary
      yPosition += 2
      const totalServiceCost = serviceRecords.reduce(
        (sum, record) => sum + (record.cost || record.total_cost || 0),
        0
      )

      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(9)
      pdf.setTextColor(RDC_ORANGE)
      pdf.text(`Total Maintenance Investment: $${totalServiceCost.toLocaleString()}`, margin, yPosition)
      yPosition += 5
    } else {
      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.setTextColor(RDC_TAUPE)
      pdf.text('No service records available', margin, yPosition)
      yPosition += 5
    }

    // ========== DOCUMENTATION SUMMARY ==========
    checkNewPage(35)
    yPosition = addHeading('DOCUMENTATION SUMMARY', yPosition)

    if (documents && documents.length > 0) {
      // Count by type
      const docsByType = {}
      documents.forEach(doc => {
        const type = doc.type || 'Other'
        docsByType[type] = (docsByType[type] || 0) + 1
      })

      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(9)
      pdf.setTextColor('0,0,0')
      pdf.text(`Total Documents: ${documents.length}`, margin, yPosition)
      yPosition += 4

      // Document types summary
      Object.entries(docsByType).forEach(([type, count]) => {
        pdf.text(`  • ${type}: ${count}`, margin + 3, yPosition)
        yPosition += 3
      })

      yPosition += 3

      // Document list
      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(9)
      pdf.setTextColor(RDC_FOREST)
      pdf.text('Documents:', margin, yPosition)
      yPosition += 3

      documents.forEach(doc => {
        checkNewPage(3)
        pdf.setFont('Helvetica', 'normal')
        pdf.setFontSize(8)
        pdf.setTextColor('0,0,0')
        const docDate = doc.date ? ` - ${format(new Date(doc.date), 'MM/dd/yyyy')}` : ''
        const docName = pdf.splitTextToSize(`${doc.name || doc.filename || 'Unnamed'}${docDate}`, contentWidth - 6)
        pdf.text(docName, margin + 3, yPosition)
        yPosition += docName.length * 2 + 1
      })
    } else {
      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.setTextColor(RDC_TAUPE)
      pdf.text('No documents available', margin, yPosition)
      yPosition += 5
    }

    // ========== VALUATION HISTORY ==========
    checkNewPage(35)
    yPosition = addHeading('VALUATION HISTORY', yPosition)

    if (valuations && valuations.length > 0) {
      // Valuation table
      const dateColW = 35
      const valueColW = 40
      const sourceColW = contentWidth - dateColW - valueColW - 2

      // Header
      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(9)
      pdf.setTextColor('255,255,255')
      pdf.setFillColor(RDC_FOREST)

      const valuationHeaders = ['Date', 'Value', 'Source']
      const valuationColWidths = [dateColW, valueColW, sourceColW]
      let vHeaderX = margin

      valuationHeaders.forEach((header, idx) => {
        pdf.rect(vHeaderX, yPosition - 2, valuationColWidths[idx], 5, 'F')
        pdf.text(header, vHeaderX + 1, yPosition + 1)
        vHeaderX += valuationColWidths[idx]
      })

      yPosition += 6

      // Valuation rows
      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(9)
      pdf.setTextColor('0,0,0')

      valuations.forEach((val, idx) => {
        checkNewPage(4)
        const date = format(new Date(val.valuation_date || val.date || val.created_at), 'MM/dd/yyyy')
        const value = `$${(val.value || val.estimated_value || 0).toLocaleString()}`
        const source = val.source || 'N/A'

        if (idx % 2 === 0) {
          pdf.setFillColor(245, 242, 237)
          pdf.rect(margin, yPosition - 2, contentWidth, 4, 'F')
        }

        pdf.text(date, margin + 1, yPosition)
        pdf.text(value, margin + dateColW + 1, yPosition)
        const sourceWrapped = pdf.splitTextToSize(source, sourceColW - 2)
        pdf.text(sourceWrapped, margin + dateColW + valueColW + 1, yPosition)

        yPosition += 4
      })

      // Current valuation highlight
      yPosition += 3
      const currentVal = valuations[valuations.length - 1]
      const currentValue = currentVal.value || currentVal.estimated_value || 0

      pdf.setFont('Helvetica', 'bold')
      pdf.setFontSize(10)
      pdf.setTextColor(RDC_ORANGE)
      pdf.text(`Current Estimated Value: $${currentValue.toLocaleString()}`, margin, yPosition)
      yPosition += 5
    } else {
      pdf.setFont('Helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.setTextColor(RDC_TAUPE)
      pdf.text('No valuation records available', margin, yPosition)
      yPosition += 5
    }

    // ========== FOOTER ==========
    const footerY = pageHeight - 12

    pdf.setFont('Helvetica', 'normal')
    pdf.setFontSize(8)
    pdf.setTextColor(RDC_TAUPE)

    // Horizontal line
    pdf.setDrawColor(RDC_GRAY)
    pdf.setLineWidth(0.3)
    pdf.line(margin, footerY - 3, pageWidth - margin, footerY - 3)

    // Footer text
    pdf.text('This report was generated by RDC Digital Garage', margin, footerY)
    pdf.text('garage.ridinganddrivingclub.com', margin, footerY + 3)
    pdf.setFontSize(7)
    pdf.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`, margin, footerY + 6)

    // Generate filename
    const filename = `${vehicle.year}_${vehicle.make}_${vehicle.model}_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`

    // Save PDF
    pdf.save(filename)
  }

  return (
    <button
      onClick={handleDownloadPDF}
      disabled={isGenerating}
      className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <FileDown size={18} />
      {isGenerating ? 'Generating Report...' : 'Download Report'}
    </button>
  )
}
